import React, { useState, useCallback, useMemo, useEffect, memo } from 'react'
import { PlayCircleFilled, FilePdfOutlined } from '@ant-design/icons'
import LightboxViewer from '../LightboxViewer/LightboxViewer'
import { apiUrl } from '../../../../config'
import {
  MediaGridContainer,
  MediaItem,
  MediaOverlay,
} from '../../NewsFeedStyled'

const loadedMediaUrls = new Set()

/** Resolve media URL */
const resolveMediaUrl = m => {
  if (!m) {
    return null
  }

  const url = m.fileUrl || m.file_url

  if (url) {
    let finalUrl = ''

    if (url.startsWith('http')) {
      finalUrl = url
    } else if (url.startsWith('/')) {
      finalUrl = `${apiUrl}${url}`
    } else if (/^[0-9a-fA-F]{24}$/.test(url)) {
      finalUrl = `${apiUrl}/api/v1/images/${url}`
    } else {
      finalUrl = `${apiUrl}/${url}`
    }

    return finalUrl
  }

  const id = m.fileId || m.file_id

  if (id) {
    const finalUrl = `${apiUrl}/api/v1/images/${id}`

    return finalUrl
  }

  return null
}

/**
 * MediaGrid - Hiển thị lưới đa phương tiện kiểu Facebook
 * @param {object} [post] - Bài viết cha; khi có sẽ mở lightbox dạng mini-post (comment/sửa caption)
 */
const MediaGrid = ({ media = [], post = null }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)
  const [isLoaded, setIsLoaded] = useState(false)

  // Click vào item → mở lightbox
  const handleMediaClick = useCallback(index => {
    setLightboxIndex(index)
    setLightboxOpen(true)
  }, [])

  // 5+ ảnh: hiện 5 ô (2 trên / 3 dưới); còn lại dùng tối đa 4 ô
  const visibleCount = media.length >= 5 ? 5 : Math.min(media.length, 4)
  const visibleMedia = useMemo(
    () => media.slice(0, visibleCount),
    [media, visibleCount]
  )

  // Preload all media at once using Promise.all and persist loaded URLs
  useEffect(() => {
    if (visibleMedia.length === 0) {
      setIsLoaded(true)
      return
    }

    const urlTypeMap = new Map()
    visibleMedia.forEach(m => {
      const url = resolveMediaUrl(m)
      if (url) {
        urlTypeMap.set(url, m.mediaType || m.media_type)
      }
    })

    const urlsToLoad = Array.from(urlTypeMap.keys()).filter(
      url => !loadedMediaUrls.has(url)
    )

    if (urlsToLoad.length === 0) {
      setIsLoaded(true)
      return
    }

    setIsLoaded(false)

    const promises = urlsToLoad.map(url => {
      const type = urlTypeMap.get(url)
      if (!type) {
        return Promise.resolve()
      }

      if (type === 'IMAGE') {
        return new Promise(resolve => {
          const img = new Image()
          img.src = url
          img.onload = () => {
            loadedMediaUrls.add(url)
            resolve()
          }
          img.onerror = () => {
            loadedMediaUrls.add(url)
            resolve()
          }
        })
      }

      if (type === 'VIDEO') {
        return new Promise(resolve => {
          const video = document.createElement('video')
          video.src = url
          video.preload = 'metadata'
          video.onloadeddata = () => {
            loadedMediaUrls.add(url)
            resolve()
          }
          video.onerror = () => {
            loadedMediaUrls.add(url)
            resolve()
          }
          setTimeout(() => {
            loadedMediaUrls.add(url)
            resolve()
          }, 3000)
        })
      }

      loadedMediaUrls.add(url)
      return Promise.resolve()
    })

    Promise.all(promises).then(() => {
      setIsLoaded(true)
    })
  }, [visibleMedia])

  if (media.length === 0) return null

  const isFivePlus = media.length >= 5
  const hasMore = isFivePlus ? media.length > 5 : media.length > 4
  const gridClass = isFivePlus
    ? 'grid-5'
    : `grid-${Math.min(media.length, 4)}`

  const getItemClassName = index => {
    if (isFivePlus) {
      return index < 2 ? 'grid-5-top' : ''
    }
    if (media.length >= 3 && media.length <= 4 && index === 0) {
      return `grid-${media.length}-0`
    }
    return ''
  }

  const overlayIndex = isFivePlus ? 4 : 3
  const overlayCount = isFivePlus
    ? media.length - 5
    : media.length - 3

  return (
    <>
      <style>{`
        @keyframes nfMediaShimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .nf-media-shimmer-item {
          background: linear-gradient(90deg, #e4e6eb 25%, #f2f3f5 50%, #e4e6eb 75%);
          background-size: 200% 100%;
          animation: nfMediaShimmer 1.5s infinite linear;
          border-radius: 8px;
        }
        .nf-media-fade-in {
          opacity: 1;
          animation: nfGridFadeIn 0.4s ease-in-out forwards;
        }
        @keyframes nfGridFadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {!isLoaded ? (
        <MediaGridContainer className={gridClass}>
          {visibleMedia.map((m, index) => (
            <MediaItem
              key={`shimmer-${m.id || index}`}
              className={`${getItemClassName(index)} nf-media-shimmer-item`}
              style={{ minHeight: media.length === 1 ? '300px' : '150px' }}
            />
          ))}
        </MediaGridContainer>
      ) : (
        <MediaGridContainer className={`${gridClass} nf-media-fade-in`}>
          {visibleMedia.map((m, index) => (
            <MediaItem
              key={m.id || index}
              className={getItemClassName(index)}
              onClick={() => handleMediaClick(index)}>
              {(m.mediaType === 'IMAGE' || m.media_type === 'IMAGE') && (
                <img
                  src={resolveMediaUrl(m)}
                  alt={m.caption || ''}
                  title={m.caption || ''}
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: media.length === 1 ? 'auto' : '100%',
                    maxHeight: media.length === 1 ? '600px' : '100%',
                    minHeight: media.length === 1 ? undefined : '100%',
                    display: 'block',
                    objectFit: media.length === 1 ? 'contain' : 'cover',
                    background: '#f0f2f5',
                  }}
                />
              )}

              {(m.mediaType === 'VIDEO' || m.media_type === 'VIDEO') && (
                <>
                  <video
                    src={resolveMediaUrl(m)}
                    preload="metadata"
                    style={{
                      objectFit: media.length === 1 ? 'contain' : 'cover',
                      background: '#000',
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      background: 'rgba(0,0,0,0.55)',
                      borderRadius: '50%',
                      width: 56,
                      height: 56,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#ffffff',
                      fontSize: 28,
                      zIndex: 2,
                    }}>
                    <PlayCircleFilled />
                  </div>
                </>
              )}

              {(m.mediaType === 'PDF' ||
                m.media_type === 'PDF' ||
                m.mediaType === 'DOCUMENT' ||
                m.media_type === 'DOCUMENT') && (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    background: '#f0f2f5',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                  <FilePdfOutlined style={{ fontSize: 48, color: '#ff4d4f' }} />
                  <div
                    style={{
                      marginTop: 8,
                      padding: '0 16px',
                      textAlign: 'center',
                      wordBreak: 'break-all',
                    }}>
                    {m.fileName || m.file_name}
                  </div>
                </div>
              )}

              {/* Overlay "+N" trên ô cuối khi còn ảnh ẩn */}
              {index === overlayIndex && hasMore && (
                <MediaOverlay>
                  <span>+{overlayCount}</span>
                </MediaOverlay>
              )}
            </MediaItem>
          ))}
        </MediaGridContainer>
      )}

      {/* Lightbox / mini-post viewer */}
      {lightboxOpen && (
        <LightboxViewer
          media={media}
          initialIndex={lightboxIndex}
          post={post}
          onClose={() => setLightboxOpen(false)}
        />
      )}

      {/* Caption ảnh đang xem gần nhất / caption ảnh đầu nếu có */}
      {!lightboxOpen &&
        (media[0]?.caption || '').trim() &&
        media.length === 1 && (
          <div
            style={{
              marginTop: 8,
              fontSize: 14,
              color: '#050505',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}>
            {media[0].caption}
          </div>
        )}
    </>
  )
}

export default memo(MediaGrid)
